import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { QUERY_ITEM_SKU } from './graphql/queries';

export const PAGE_SIZE = 50;
const DEFAULT_SORT_BY = { id: 'sku', desc: false };

const buildOrderBy = (sortBy) => {
    const direction = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
    switch (sortBy?.id) {
        case 'sku':
            return [{ sku: direction }];
        case 'description':
            return [{ description: direction }, { sku: 'asc_nulls_last' }];
        case 'service_time':
            return [{ service_time: direction }, { sku: 'asc_nulls_last' }];
        case 'weight':
            return [{ weight: direction }, { sku: 'asc_nulls_last' }];
        case 'dimensions':
            return [{ cubes: direction }, { sku: 'asc_nulls_last' }];
        case 'inventory':
            return [{ inventory_min: direction }, { sku: 'asc_nulls_last' }];
        default:
            return [{ sku: 'asc_nulls_last' }];
    }
};

export const useSkus = ({ clientRef, isCarrierAdmin, organizationId, selectedClientId }) => {
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [refreshToken, setRefreshToken] = useState(0);

    const [getSkus, { data, loading, error }] = useLazyQuery(QUERY_ITEM_SKU, {
        fetchPolicy: 'network-only',
    });

    const getSkusDebounced = useMemo(
        () =>
            _.debounce((payload) => {
                return getSkus(payload);
            }, 300),
        []
    );

    useEffect(() => () => getSkusDebounced.cancel(), [getSkusDebounced]);

    useEffect(() => {
        if (!error) return;
        console.error(error);
        captureException(error);
    }, [error]);

    // Carrier admins see the whole org (via client.user.organization_id), optionally narrowed to one
    // entity by the dropdown; everyone else sees their own client.
    const scope = useMemo(() => {
        if (isCarrierAdmin) {
            if (selectedClientId) return { client_id: { _eq: selectedClientId } };
            return { client: { user: { organization_id: { _eq: organizationId } } } };
        }
        return { client_id: { _eq: clientRef } };
    }, [isCarrierAdmin, organizationId, selectedClientId, clientRef]);

    const ready = isCarrierAdmin ? !!organizationId : !!clientRef;

    useEffect(() => {
        setPage(0);
    }, [scope, searchTerm, sortBy]);

    const order_by = useMemo(() => buildOrderBy(sortBy), [sortBy]);

    useEffect(() => {
        if (!ready) return;

        const trimmed = searchTerm.trim();
        const sku_like = trimmed ? `%${trimmed}%` : '%';

        getSkusDebounced({
            variables: {
                where: { _and: [scope, { sku: { _ilike: sku_like } }] },
                limit: PAGE_SIZE,
                offset: page * PAGE_SIZE,
                order_by,
            },
        });
    }, [ready, scope, page, searchTerm, order_by, refreshToken, getSkusDebounced]);

    const skus = useMemo(() => data?.results || [], [data]);
    const totalCount = data?.aggregate?.aggregate?.count || 0;

    // Re-run the current query (e.g. after a mutation) without resetting paging/sort.
    const refetch = useCallback(() => setRefreshToken((token) => token + 1), []);

    return {
        skus,
        totalCount,
        loading,
        page,
        setPage,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        refetch,
    };
};
